import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DashboardGroup, STATUS_GROUP_PAGE_DATA_KEYS, StatusGroupPageData} from '@models/dashboard/dashboard-group';
import {User} from '@models/user/user';
import { HttpResponse } from '@angular/common/http';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {StorageService} from '@services/storage/storage.service';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {DashboardLayouts} from '@models/dashboard/dashboard-layouts';
import {ActivatedRoute, Event, NavigationEnd, Router} from '@angular/router';
import {Role} from '@models/user/role';
import {Observable, of, Subject, Subscription, throwError} from 'rxjs';
import {MenuItem} from '@models/sidebar/menu-item';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {GeocodingService} from '@services/administration/machine/geocoding/geocoding.service';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {
  StatusEventGroupPageDataService
} from '@services/pages/status-event-group-page/data/status-event-group-page-data.service';

import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {SettingsService} from '@services/administration/settings/settings.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {MachineListContainerComponent} from '@components/dashboard/list/machine-list-container/machine-list-container.component';

@Component({
    selector: 'scitis-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {

  //layout
  dashboardLayouts = DashboardLayouts;
  layout: string = this.dashboardLayouts.TILE;
  location: string = '';
  machineCount: number = 0;

  allChildGroups: DashboardGroup[] = [];
  currentMachineGroupId: string = '';
  currentMachineGroupName: string = '';

  isAdminRole: boolean = false;
  isSuperAdminRole: boolean = false;
  isAnalyticsRole: boolean = false;
  isWindowContentVisible: boolean = true;
  user: User = new User();
  subscription = new Subscription();
  menuRoutesSubscription: Subscription = new Subscription();
  routes: MenuItem[] = [];

  public locationsAggregate: Map<string, number> = new Map<string, number>();
  public categoriesAggregate: Map<string, MachineCategoryColumnConfiguration> = new Map<string, MachineCategoryColumnConfiguration>();
  public availableMachineCategories: MachineCategoryColumnConfiguration[] = [];
  public liveDataStreamingEnabled: boolean = true;
  public selectedMachineCategoryName: string = '';
  @ViewChild(MachineListContainerComponent)
  private machineListContainer?: MachineListContainerComponent;
  private organizationId: number = -1;
  private rootMachineryGroupId: string = '';
  private destroy: Subject<boolean> = new Subject<boolean>();
  private whiteListedMachineryGroups: number[] | null = null;
  private MINIMAL_NUMBER_GROUP_IDS: number = 10;
  private groupIdsInFirstBatch: number[] = [];
  private groupIdsForLoadMore: number[] = [];

  constructor(private activatedRoute: ActivatedRoute,
              private firebaseAuthenticationService: FirebaseAuthenticationService,
              private loadingIndicator: LoadingIndicatorService,
              private storage: StorageService,
              private dashboardService: DashboardService,
              private router: Router,
              private breadcrumbService: BreadcrumbService,
              private notificationService: NotificationService,
              private translateService: TranslateService,
              private geocoding: GeocodingService,
              private settingsService: SettingsService,
              private menuNotificationService: MenuNotificationService,
              private statusEventGroupPageDataService: StatusEventGroupPageDataService,
  ) {
    this.watchForParamUpdate();
  }

  ngOnInit(): void {
    this.currentMachineGroupId = this.getParameterValueFromUrl('id');
    this.readDashboardLayoutMode();
    this.setAuthenticatedUserDetails();
    this.listenForLeftMenuRoutes();
    this.geocoding.googleMapsLoaded.pipe(takeUntil(this.destroy)).subscribe(data => {
      if (!data) {
        this.geocoding.loadMapsApi();
      }
    });
  }

  ngOnDestroy() {
    this.menuRoutesSubscription.unsubscribe();
    this.destroy.next(true);
    this.destroy.complete();
  }

  @HostListener('document:visibilitychange')
  visibilitychange() {
    if (document.hidden) {
      this.isWindowContentVisible = false;
      console.log('Page is hidden');
    } else {
      this.isWindowContentVisible = true;
      console.log('Page is visible');
    }
  }

  selectLayout(layout: DashboardLayouts) {
    this.layout = layout;
    this.saveDashboardLayoutMode(layout);
  }

  public get selectedMachineCategoryConfig(): MachineCategoryColumnConfiguration | null {
    const categoryName = this.selectedMachineCategoryName || Array.from(this.categoriesAggregate.keys())[0];
    return categoryName ? this.categoriesAggregate.get(categoryName) ?? null : null;
  }

  public get selectedMachineCategoryIcon(): string {
    return this.selectedMachineCategoryConfig?.categoryIcon ?? '';
  }

  public onMachineCategorySelect(categoryName: string): void {
    this.selectedMachineCategoryName = categoryName;
    this.machineListContainer?.onCategorySelect(categoryName);
  }

  public onMachineCategoryChanged(categoryName: string): void {
    this.selectedMachineCategoryName = categoryName;
  }

  getDashboardDetails(groupId: string, user: User): void {
    this.getDashboardDetailsFromElasticSearch(groupId, user);
  }

  readDashboardLayoutMode(): void {
    this.storage.readValueFromIndexedDb<string>('dashboardLayout').then(
        (layout) => {
          if (layout === undefined) {
            this.layout = this.dashboardLayouts.TILE;
          } else {
            this.layout = JSON.parse(layout);
          }
        }
    ).catch((error) => {
      console.error('Error getting layout from indexedDb. using ' + this.dashboardLayouts.TILE, error);
      this.layout = this.dashboardLayouts.TILE;
    });
  }

  saveDashboardLayoutMode(layout: string): void {
    this.storage.writeValueToIndexedDb('dashboardLayout', layout);
  }

  onLocationChange(location: string = '') {
    this.loadingIndicator.show();
    let machineryGroupId: string = this.currentMachineGroupId;
    if (!machineryGroupId || machineryGroupId.isNullOrWhitespace()) {
      machineryGroupId = this.rootMachineryGroupId;
    }
    this.location = location;
    this.getMachinesCount(machineryGroupId, this.organizationId, this.location, this.whiteListedMachineryGroups);
  }

  public isCurrentMachineGroup(groupId: number): boolean {
    return String(groupId) === this.currentMachineGroupId;
  }

  public onMachineGroupChange(group: DashboardGroup): void {
    if (!group || this.isCurrentMachineGroup(group.groupId)) {
      return;
    }

    this.loadingIndicator.show(this.translateService.instant('Loading'));
    this.dashboardService.getItemMenuItemAndParentMenuItem(String(group.groupId), false)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (menuItem: MenuItem) => {
          this.menuNotificationService.broadcastClickOnDashboard(menuItem);
          this.storage.writeValueToIndexedDb('activeGroupName', group.name)
            .then(() => this.router.navigateByUrl('/', {skipLocationChange: true}))
            .then(() => this.router.navigate(['dashboard', {id: group.groupId, machineGroupName: group.name}]))
            .catch((error) => {
              this.loadingIndicator.hide();
              console.error('Dashboard. Error navigating to selected machine group.', error);
            });
        },
        error: (error) => {
          this.loadingIndicator.hide();
          console.error('Dashboard. Error selecting machine group.', error);
        }
      });
  }

  loadMoreMachineGroupsDataForStatusPage() {
    this.loadingIndicator.show();
    this.getMinimalStatusEventGroupPageInfo(this.groupIdsForLoadMore, false)
    .pipe(takeUntil(this.destroy))
    .subscribe({
      next: result => {
        this.setStatusPageDataIntoGroup(result, this.groupIdsForLoadMore);
        this.loadingIndicator.hide();
      }, error: () => {
        this.setStatusPageDataIntoGroup({}, this.groupIdsForLoadMore);
        this.loadingIndicator.hide();
        console.error('Error fetching data for load more machine groups on dashboard');
      }
    });
  }

  private getParameterValueFromUrl(parameterName: string): string {
    let value = this.activatedRoute.snapshot.paramMap.get(parameterName);
    if (value === null || value === undefined) {
      value = '';
    }
    return value;
  }

  private setAuthenticatedUserDetails() {
    this.loadingIndicator.show();

    this.firebaseAuthenticationService.getLoggedUserDetails().subscribe({
      next: (response: User) => {
        this.user = response;
        if (this.user.parentId) {
          this.getGroupNameFromStorage(this.user);
          const userRole = new Role(this.user);
          this.isSuperAdminRole = userRole.isSuperAdminRole || userRole.isTechAdminRole;
          this.isAdminRole = userRole.isAdminRole;
          this.isAnalyticsRole = userRole.isAnalyticsRole;
          this.loadingIndicator.hide();
          this.organizationId = userRole.isSuperAdminRole ? -1 : this.user.parentId;
          this.rootMachineryGroupId = String(response.machineGroup);
          this.whiteListedMachineryGroups = userRole.isSuperAdminRole ? null : this.user.getWhiteListedMachineryGroups();
          this.getDashboardDetails(this.currentMachineGroupId, this.user);
        } else {
          console.error('Dashboard. current user has no parent id defined. User: ' + this.user.displayName + '(' + this.user.username + ')');
          this.loadingIndicator.hide();
        }
      }, error: (error: HttpResponse<any>) => {
        console.error('Dashboard. Error getting current user details.', JSON.stringify(error));
      }
    });
  }

  private getMachineryGroupsFromElastic(machineryGroupId: string, organizationId: number | null, user: User) {
    const machineryGroupsObservable = this.dashboardService.getDashboardGroupsFromElastic(machineryGroupId, organizationId, user);
    machineryGroupsObservable
    .pipe(
        takeUntil(this.destroy),
        switchMap((success: DashboardGroup[]) => {
          this.allChildGroups = success;
          this.loadingIndicator.hide();
          return this.getDataForFirstBatch();
        }),
    )
    .subscribe({
      next: (result: { [key: string]: StatusGroupPageData }) => {
        this.setStatusPageDataIntoGroup(result, this.groupIdsInFirstBatch);
      }, error: (error) => {
        const message = this.translateService.instant('There was a problem loading elastic search item of group') + ' ' + machineryGroupId;
        this.notificationService.error(message, error, 'Dashboard', true);
        this.loadingIndicator.hide();
      }
    });
  }

  private getDataForFirstBatch(): Observable<any> {
    if (this.allChildGroups?.length) {
      this.splitGroupIdsIntoBatches();
      if (this.groupIdsInFirstBatch?.length) {
        return this.getMinimalStatusEventGroupPageInfo(this.groupIdsInFirstBatch, true);
      }
    }
    return of([]);
  }

  private splitGroupIdsIntoBatches(): void {
    const allGroupsCopy = [...this.allChildGroups];
    this.groupIdsInFirstBatch = allGroupsCopy.splice(0, this.MINIMAL_NUMBER_GROUP_IDS).map(item => item.groupId);
    this.groupIdsForLoadMore = allGroupsCopy.map(item => item.groupId);
  }

  private getMinimalStatusEventGroupPageInfo(batch: number[], isFirstBatch: boolean): Observable<any> {
    return this.statusEventGroupPageDataService.getMinimalStatusEventGroupPageInfoByBulk(batch, isFirstBatch)
    .pipe(catchError(err => {
      return throwError(() => err);
    }));
  }

  private setStatusPageDataIntoGroup(mapData: { [key: number]: StatusGroupPageData }, batchIds: number[]) {
    this.allChildGroups = this.allChildGroups.map((group) => {
      const isPartOfTheBatch = batchIds.some(id => id === group.groupId);
      if (isPartOfTheBatch) {
        if (mapData[group.groupId]) {
          group.statusGroupPageData = mapData[group.groupId];
        } else if (!group?.statusGroupPageData && !mapData[group.groupId]) {
          group.statusGroupPageData = {[STATUS_GROUP_PAGE_DATA_KEYS.OFFLINE]: 1};
        }
      }
      return group;
    });
  }

  private getDashboardDetailsFromElasticSearch(groupId: string, user: User): void {
    this.loadingIndicator.show();
    let machineryGroupId: string = groupId;
    if (!machineryGroupId || machineryGroupId.isNullOrWhitespace()) {
      machineryGroupId = String(user.machineGroup);
    }

    this.machineCount = 0;
    this.allChildGroups = [];

    const whiteListedMachineryGroupsForUser: number[] | null =
        user.getWhiteListedMachineryGroups(true).length == 0 || this.isSuperAdminRole ? null : user.getWhiteListedMachineryGroups();

    this.getMachinesCount(machineryGroupId, this.organizationId, this.location, this.user.getWhiteListedMachineryGroups(true));
    this.getLocationsAndCategoriesAggregates(machineryGroupId, this.organizationId, whiteListedMachineryGroupsForUser);
    this.getMachineryGroupsFromElastic(machineryGroupId, null, this.user);
  }

  private getGroupNameFromStorage(user: User | undefined) {

    if (this.currentMachineGroupId === null || this.currentMachineGroupId.length === 0) {
      if (user != undefined) {
        this.currentMachineGroupName = user.machineGroupName;
        return;
      }
    } else {

      this.storage.readValueFromIndexedDb<string>('activeGroupName').then(
          name => {
            if (name !== null && undefined !== name) {
              this.currentMachineGroupName = JSON.parse(name);
            }
          },
          (error) => {
            console.log('Error', error);
          });
    }
  }

  private getMachinesCount(machineryGroupId: string, organizationId: number, location: string, whiteListedMachineryGroups: number[] | null) {
    this.dashboardService.countDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINE, machineryGroupId, organizationId, location, whiteListedMachineryGroups)
    .pipe(takeUntil(this.destroy))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        this.machineCount = success.result.totalHits;
      }
    });
  }


  private watchForParamUpdate(): void {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        if (!event.urlAfterRedirects || !event.urlAfterRedirects.startsWith('/dashboard')) {
          return;
        }

        const idFromUrl = this.getParameterValueFromUrl('id');
        if (this.currentMachineGroupId === idFromUrl) {
          return;
        }
      }
    });
  }

  private listenForLeftMenuRoutes(): void {
    this.menuRoutesSubscription = this.breadcrumbService.routesNotification
    .subscribe((menuItems: MenuItem[]) => {
      this.routes = menuItems;
    });
  }

  private getLocationsAndCategoriesAggregates(machineryGroupId: string, organizationId: number, whiteListedMachineryGroups: number[] | null) {
    this.settingsService.isLiveDataStreamingEnabled()
    .pipe(switchMap((result) => {
      this.liveDataStreamingEnabled = result;
      return this.dashboardService.getDashboardItemsAggregateDetailsForGroup(machineryGroupId, organizationId, whiteListedMachineryGroups);
    }))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        this.locationsAggregate = this.parseAndFilterLocationsAggregate(success);
        this.dashboardService.getMachineCategoriesConfiguration().pipe(takeUntil(this.destroy)).subscribe({
          next: availableMachineCategories => {
            this.availableMachineCategories = availableMachineCategories;
            this.categoriesAggregate = this.parseAndEnhanceCategoriesAggregate(success, availableMachineCategories);
          }, error: (_error) => {
            this.availableMachineCategories = [];
            this.categoriesAggregate = this.parseAndEnhanceCategoriesAggregate(success, []);
          }
        });

      }, error: (error) => {
        const message = this.translateService.instant('There was a problem loading elasticsearch aggregated locations and categories of group') + ' ' + machineryGroupId;
        this.notificationService.error(message, error, 'Dashboard', true);
      }
    });
  }


  private parseAndFilterLocationsAggregate(response: ElasticSearchResponseEntity): Map<string, number> {
    const result: Map<string, number> = new Map<string, number>();
    if (!response || !response.result || !response.result.aggregations || !response.result.aggregations.location) {
      return result;
    }

    const locations = response.result.aggregations.location;
    const keys = Object.keys(locations);

    for (const key of keys) {
      if (!key || key.isNullOrWhitespace()) {
        continue;
      }
      let location: string = '';
      const value = locations[key];
      if (key.indexOf(',') < 0) {
        location = key.capitalizeFirstLetter();

      } else {
        let [country, city] = key.split(',');

        city = city.trim();
        country = country.trim();

        location = country.toUpperCase() + ', ' + city.capitalizeFirstLetter();
      }
      result.set(location, value);
    }
    return result;
  }

  private parseAndEnhanceCategoriesAggregate(response: ElasticSearchResponseEntity, categories: MachineCategoryColumnConfiguration[]): Map<string, MachineCategoryColumnConfiguration> {
    const result: Map<string, MachineCategoryColumnConfiguration> = new Map<string, MachineCategoryColumnConfiguration>();
    if (!response || !response.result || !response.result.aggregations || !response.result.aggregations.category) {
      return result;
    }
    const categoriesAggregate = response.result.aggregations.category;
    const keys = Object.keys(categoriesAggregate);
    for (const key of keys) {
      if (!key || key.isNullOrWhitespace()) {
        continue;
      }

      const machineCategory = this.findCategoryByName(key, categories);
      machineCategory.machinesCount = categoriesAggregate[key];
      result.set(machineCategory.categoryName, machineCategory);
    }
    return result;
  }

  private findCategoryByName(category: string, categories: MachineCategoryColumnConfiguration[]): MachineCategoryColumnConfiguration {
    let machineCategory = categories.find(c => c.categoryName.toUpperCase() === category.toUpperCase());
    if (!machineCategory) {
      machineCategory = new MachineCategoryColumnConfiguration();
      machineCategory.categoryName = category.toUpperCase();
      machineCategory.categoryDisplayName = category;
    }
    return machineCategory;
  }

}
